import { Grid, Typography, ButtonBase as RippleEffect } from '@mui/material';
import React, { useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import { PlatformLoadingSkeleton } from './Platform/PlatformLoadingSkeleton';
import { PlatformCard } from './Platform';
import { AllPlatformsQuery } from '@/__generated__/graphql';

type SortByOption = 'high-to-low' | 'low-to-high';

type PlatformProps = {
  loading: boolean;
  selectedPlatforms: AllPlatformsQuery['allPlatforms'];
  error: ApolloError | undefined;
  handlePlatformChange: (id: string, name: string) => void;
  sortBy: SortByOption;
  platformSelectedFilter: string;
  selectedDeploymentTypes?: string[];
  categoryDataTypes?: Record<string, string[]>;
};

export const GridView = ({
  loading,
  selectedPlatforms,
  error,
  handlePlatformChange,
  sortBy,
  platformSelectedFilter,
  selectedDeploymentTypes = [],
  categoryDataTypes = {},
}: PlatformProps) => {

  const isCoatingConditionFilter = (filter: string): boolean => {
    return filter === 'coating-health';
  };
  // Calculate the maximum total value across all platforms for the selected filter
  const maxValue = useMemo(() => {
    if (
      !selectedPlatforms ||
      selectedPlatforms.length === 0 ||
      isCoatingConditionFilter(platformSelectedFilter)
    )
      return undefined;

    let maxTotal = 0;
    selectedPlatforms.forEach((platform) => {
      if (platform.platformCardInsights && platform.platformCardInsights.length > 0) {
        const mappedData = platform.platformCardInsights.reduce((accumulator, current) => {
          // Keep group names as-is, use 'coating-health' as the key (not 'coating condition')
          const groupKey = current.group.toLowerCase();
          if (!accumulator[groupKey]) {
            accumulator[groupKey] = [];
          }
          accumulator[groupKey].push(current);
          return accumulator;
        }, {} as Record<string, typeof platform.platformCardInsights>);

        // Handle coating condition filter - use 'coating-health' as the key
        const isCoatingFilter = isCoatingConditionFilter(platformSelectedFilter);
        const filterKey = isCoatingFilter ? 'coating-health' : platformSelectedFilter;
        const cardInsights = mappedData[filterKey];

        if (cardInsights && cardInsights.length > 0) {
          const total = cardInsights.reduce((accumulator, item) => {
            return accumulator + Number(item.value);
          }, 0);

          if (total > maxTotal) {
            maxTotal = total;
          }
        }
      }
    });

    return maxTotal > 0 ? maxTotal : undefined;
  }, [selectedPlatforms, platformSelectedFilter]);

  // Sort platforms based on their total values for the selected filter
  const sortedPlatforms = useMemo(() => {
    if (!selectedPlatforms || selectedPlatforms.length === 0) return [];

    const platformsWithTotals = selectedPlatforms.map((platform) => {
      let total = 0;

      if (platform.platformCardInsights && platform.platformCardInsights.length > 0) {
        const mappedData = platform.platformCardInsights.reduce((accumulator, current) => {
          // Keep group names as-is, use 'coating-health' as the key (not 'coating condition')
          const groupKey = current.group.toLowerCase();
          if (!accumulator[groupKey]) {
            accumulator[groupKey] = [];
          }
          accumulator[groupKey].push(current);
          return accumulator;
        }, {} as Record<string, typeof platform.platformCardInsights>);

        // Handle coating condition filter - use 'coating-health' as the key
        const isCoatingFilter = isCoatingConditionFilter(platformSelectedFilter);
        const filterKey = isCoatingFilter ? 'coating-health' : platformSelectedFilter;
        const cardInsights = mappedData[filterKey];

        if (cardInsights && cardInsights.length > 0) {
          total = cardInsights.reduce((accumulator, item) => {
            return accumulator + Number(item.value);
          }, 0);
        }
      }

      return { ...platform, total };
    });

    return platformsWithTotals.sort((a, b) => {
      const totalA = a.total || 0;
      const totalB = b.total || 0;
      if (sortBy === 'high-to-low') {
        return totalB - totalA;
      } else {
        return totalA - totalB;
      }
    });
  }, [selectedPlatforms, platformSelectedFilter, sortBy]);

  return (
    <Grid sx={{ my: 2 }} justifyContent="center" container spacing={3.5} data-testid="platforms">
      {loading && <PlatformLoadingSkeleton />}
      {((!loading && selectedPlatforms.length === 0) || error) && (
        <Typography variant="body1" fontSize="2.6rem" my={10} fontWeight={600}>
          No platforms found
        </Typography>
      )}
      {!loading &&
        selectedPlatforms.length > 0 &&
        sortedPlatforms.map(({ name, id, platformCardInsights, coverImagePath }) => (
          <Grid item lg={3} md={3} sm={6} xs={12} key={id}>
            <RippleEffect
              component="div"
              onClick={() => handlePlatformChange(id, name)}
              sx={{ borderRadius: '10px', display: 'block' }}
            >
              <PlatformCard
                name={name}
                subHeading={
                  platformSelectedFilter === 'risk score' ? 'Score by area and POI' : ''
                }
                platformCardInsights={platformCardInsights}
                imageUrl={coverImagePath}
                maxValue={maxValue}
                platformSelectedFilter={platformSelectedFilter}
                selectedDeploymentTypes={selectedDeploymentTypes}
                categoryDataTypes={categoryDataTypes}
              ></PlatformCard>
            </RippleEffect>
          </Grid>
        ))}
    </Grid>
  );
};
